import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { translateTag } from "@/utils/tagTranslations";

// City clusters for cross-linking related articles between neighbouring cities.
const CITY_CLUSTERS = {
  caucasus: ["tbilisi", "batumi", "yerevan", "baku"],
  italy: ["florence", "capri", "lake-como", "portofino", "amalfi"],
  greek: ["mykonos", "santorini", "rhodes", "crete"],
  turkey: ["antalya", "alanya", "bodrum", "marmaris", "istanbul", "mersin", "fethiye", "cesme"],
  spain: ["barcelona", "alicante", "ibiza", "malaga", "valencia", "costa-brava", "tenerife", "mallorca"],
  mediterranean: ["limassol", "rhodes", "santorini", "mykonos"],
  adriatic: ["hvar", "kotor", "budva", "dubrovnik"],
  riviera: ["nice", "cannes", "monaco", "saint-tropez"],
  vietnam: ["danang", "nhatrang", "hanoi"],
};

const CLUSTER_SUBTITLES = {
  caucasus: {
    ru: "Доставка цветов в другие города Кавказа",
    en: "Flower delivery to other cities in the Caucasus",
    tr: "Kafkasya'daki diğer şehirlere çiçek teslimatı",
  },
  italy: {
    ru: "Доставка цветов в другие города Италии",
    en: "Flower delivery to other cities in Italy",
    tr: "İtalya'daki diğer şehirlere çiçek teslimatı",
  },
  greek: {
    ru: "Доставка цветов на другие греческие острова",
    en: "Flower delivery to other Greek islands",
    tr: "Diğer Yunan adalarına çiçek teslimatı",
  },
  turkey: {
    ru: "Доставка цветов в другие города Турции",
    en: "Flower delivery to other cities in Turkey",
    tr: "Türkiye'deki diğer şehirlere çiçek teslimatı",
  },
  spain: {
    ru: "Доставка цветов в другие города Испании",
    en: "Flower delivery to other cities in Spain",
    tr: "İspanya'daki diğer şehirlere çiçek teslimatı",
  },
  mediterranean: {
    ru: "Доставка цветов на другие курорты Средиземноморья",
    en: "Flower delivery to other Mediterranean resorts",
    tr: "Diğer Akdeniz tatil beldelerine çiçek teslimatı",
  },
  adriatic: {
    ru: "Доставка цветов на другие курорты Адриатики",
    en: "Flower delivery to other Adriatic resorts",
    tr: "Diğer Adriyatik tatil beldelerine çiçek teslimatı",
  },
  riviera: {
    ru: "Доставка цветов в другие города Французской Ривьеры",
    en: "Flower delivery to other cities on the French Riviera",
    tr: "Fransız Rivierası'ndaki diğer şehirlere çiçek teslimatı",
  },
  vietnam: {
    ru: "Доставка цветов в другие города Вьетнама",
    en: "Flower delivery to other cities in Vietnam",
    tr: "Vietnam'daki diğer şehirlere çiçek teslimatı",
  },
};

const getClusterKey = (citySlug) =>
  Object.keys(CITY_CLUSTERS).find((key) => CITY_CLUSTERS[key].includes(citySlug));

export const RelatedCityArticles = ({ citySlug, lang = "ru" }) => {
  const [articles, setArticles] = useState([]);
  const clusterKey = getClusterKey(citySlug);
  const cluster = clusterKey ? CITY_CLUSTERS[clusterKey] : null;

  useEffect(() => {
    if (!cluster) return;
    const others = cluster.filter((c) => c !== citySlug);
    const fetchArticles = async () => {
      try {
        const res = await fetch(`/api/blog/posts?city=${others.join(",")}&limit=8`);
        const data = await res.json();
        setArticles((data.articles || []).slice(0, 4));
      } catch {
        // silently fail
      }
    };
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlug]);

  if (!cluster || articles.length === 0) return null;

  const titleMain = lang === "tr" ? "Benzer makaleler" : lang === "en" ? "Related articles" : "Похожие статьи";
  const subtitleSet = CLUSTER_SUBTITLES[clusterKey] || CLUSTER_SUBTITLES.caucasus;
  const subtitle = subtitleSet[lang] || subtitleSet.ru;
  const readMore = lang === "tr" ? "Oku" : lang === "en" ? "Read" : "Читать";
  const langSuffix = lang !== "ru" ? `?lang=${lang}` : "";

  return (
    <section className="py-12 sm:py-16 bg-gray-50" data-testid="related-city-articles">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-6 sm:mb-8">
          <h2
            className="text-2xl sm:text-3xl font-bold text-gray-900"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            <MapPin className="w-6 h-6 text-pink-500 inline-block mr-2 -mt-1" />
            {titleMain}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {articles.map((article) => {
            const title = lang === "tr" && article.title_tr ? article.title_tr : lang === "en" && article.title_en ? article.title_en : article.title_ru;
            const excerpt = lang === "tr" && article.excerpt_tr ? article.excerpt_tr : lang === "en" && article.excerpt_en ? article.excerpt_en : article.excerpt_ru;
            return (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}${langSuffix}`}
                className="group block p-4 sm:p-5 rounded-2xl border border-gray-100 hover:border-pink-200 hover:shadow-lg transition-all bg-white"
                data-testid={`related-article-${article.slug}`}
              >
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(article.tags || []).slice(0, 2).map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-pink-50 text-pink-600 font-medium">
                      {translateTag(tag, lang)}
                    </span>
                  ))}
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors line-clamp-2">
                  {title}
                </h3>
                <p className="text-xs text-gray-500 line-clamp-2 mb-3">{excerpt}</p>
                <span className="text-xs font-semibold text-pink-500 group-hover:text-pink-600 flex items-center gap-1">
                  {readMore} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedCityArticles;
